import { Injectable } from '@angular/core';
import { DropdownListRenderer } from 'src/app/shared/renderers/dropdown-list/dropdown-list.component';
import { DeleteButtonRenderer } from 'src/app/shared/renderers/delete-button/delete-button.component';
import { InputSearchRenderer } from 'src/app/shared/renderers/input-search/input-search.component';
import { ViewButtonRenderer } from 'src/app/shared/renderers/view-button/view-button.component';
import { CalendarRenderer } from 'src/app/shared/renderers/calendar/calendar.component';
import { Utils } from 'src/app/shared/utils/utils';
import { ColDef, ColGroupDef, RowNode } from 'ag-grid-community';
import { Constants } from 'src/app/shared/utils/constants';
import { CropDropdownListRenderer } from '../shared/renderers/crop-dropdown-list/crop-dropdown-list.component';
import { Project } from 'src/app/shared/models/project';
import { ButtonExpandGridRenderer } from 'src/app/shared/renderers/button-expand-grid/button-expand-grid.component';
import { InputNumberRenderer } from 'src/app/shared/renderers/input-number/input-number.component';
import { InputTextRenderer } from 'src/app/shared/renderers/input-text/input-text.component';
import { ListboxRenderer } from 'src/app/shared/renderers/listbox/listbox.component';
import { DuplicateButtonRenderer } from 'src/app/shared/renderers/duplicate-button/duplicate-button.component';
import { InputTextApplicationSchemeRenderer } from 'src/app/shared/renderers/input-text/input-text-application-scheme/input-text-application-scheme.component';
import { MitigationCheckboxRenderer } from 'src/app/environmental-exposure-assessment/eea-gap/shared/renderers/mitigation-checkbox/mitigation-checkbox.component';
import { MitigationCheckboxHeaderComponent } from 'src/app/environmental-exposure-assessment/eea-gap/shared/mitigation-checkbox-header/mitigation-checkbox-header.component';
import { EndpointGet } from 'src/app/shared/models/endpoint';
import { InfoGridHeaderComponentComponent } from 'src/app/shared/components/info-grid-header-component/info-grid-header-component.component';
import { BufferDropdownListRenderer } from '../shared/renderers/buffer-dropdown-list/buffer-dropdown-list.component';
import { SprayDriftOptionsRenderer } from '../shared/renderers/spray-drift-options/spray-drift-options.component';
import { CropList } from 'src/app/shared/models/crop-list';

@Injectable({
    providedIn: 'root'
})
export class EEAGapSurfaceWaterColdef {

    constructor() { }

    public getColumnsDefinition(options: any, isProjectOwnershipValid: boolean, cropListEVA: CropList[]): (ColDef | ColGroupDef)[] {
        return [
            {
                headerName: '',
                children: [
                    {
                        headerName: '',
                        field: 'select',
                        type: 'default',
                        headerCheckboxSelection: isProjectOwnershipValid,
                        checkboxSelection: true,
                        width: 50,
                        hide: false,
                        editable: false,
                        pinned: 'left',
                        cellStyle: () => {
                            return !isProjectOwnershipValid ? { pointerEvents: 'none', backgroundColor: '#f0f0f0' }
                                : undefined;
                        }
                    },
                    {
                        headerName: 'Application Scheme',
                        headerComponent: InfoGridHeaderComponentComponent,
                        headerComponentParams: {
                            name: 'Application Scheme',
                            infoText: Constants.MESSAGES.APP_SCHEME_INFO,
                            popupWidth: 450
                        },
                        wrapText: true,
                        field: 'name',
                        type: 'default',
                        resizable: true,
                        pinned: 'left',
                        width: 300,
                        filter: 'agSetColumnFilter',
                        cellRenderer: InputTextApplicationSchemeRenderer,
                        cellRendererParams: {
                            disabled: !isProjectOwnershipValid,
                            isRequired: true
                        },
                        editable: false,
                        cellEditorParams: {
                            maxLength: 200,
                        },
                    },
                    {
                        headerName: options?.geographiesList[0]?.source,
                        wrapText: true,
                        field: 'geographies',
                        type: 'default',
                        filter: 'agSetColumnFilter',
                        // hide: ((JSON.stringify(options.selectedModels) === JSON.stringify(new Array(Constants.MODELS.UK)))) || (options.geographySelected !== Constants.CROP_GEOGRAPHIES.USA), // TODO: Luis Miranda - uncomment when the new Geographies component is implemented
                        hide: true,
                        filterParams: {
                            cellRenderer: (pParams: any) => {
                                return Utils.formatFilterValues(pParams, options.geographiesList, 'key', 'name')
                            },
                        },

                        cellRenderer: ListboxRenderer,
                        cellRendererParams: {
                            options: options.geographiesList,
                            optionValue: 'key',
                            optionLabel: 'name',
                            checkbox: true,
                            filter: false,
                            multiple: true,
                            disabled: false,
                            validateModel: false,
                            compartment: Constants.COMPARTMENTS.SURFACE_WATER,
                        },
                        resizable: true,
                        sortable: true,
                        width: 150,
                        editable: isProjectOwnershipValid,
                    },
                    {
                        headerName: 'Crop (AppDate)',
                        wrapText: true,
                        field: 'cropAppDatePk',
                        type: 'select',
                        resizable: true,
                        width: 200,
                        editable: false,
                        hide: (options.geographySelected == Constants.CROP_GEOGRAPHIES.USA) || (options.geographySelected == Constants.CROP_GEOGRAPHIES.UK),
                        filter: 'agSetColumnfilter',
                        filterParams: {
                            cellRenderer: (pParams: any) => {
                                return Utils.formatFilterValues(pParams, options.cropList, 'CropListPk', 'CropName')
                            },
                        },
                        cellRenderer: CropDropdownListRenderer,
                        cellRendererParams: (params: any) => {
                            return {
                                options: options.cropList,
                                purpose: Constants.CROP_PURPOSES.APP_DATE,
                                matchings: options.cropListMatching,
                                compartment: Constants.COMPARTMENTS.SURFACE_WATER,
                                geography: options.geographySelected,
                                optionValue: 'CropListPk',
                                optionLabel: 'CropName',
                                columnThatModifies: 'cropAppDatePk',
                                reloadCore: params?.data?.reloadCore,
                                disabled: !isProjectOwnershipValid,
                            }
                        },
                    },
                    {
                        headerName: 'Crop (Drift)',
                        wrapText: true,
                        field: 'cropInterceptionPk',
                        hide: (options.geographySelected != Constants.CROP_GEOGRAPHIES.UK),
                        resizable: true,
                        width: 220,
                        type: 'select',
                        editable: false,
                        cellRenderer: CropDropdownListRenderer,
                        cellRendererParams: (params: any) => {
                            return {
                                options: options.cropList,
                                purpose: Constants.CROP_PURPOSES.INTERCEPTION,
                                matchings: options.cropListMatching,
                                compartment: Constants.COMPARTMENTS.SURFACE_WATER,
                                geography: options.geographySelected,
                                optionValue: 'CropListPk',
                                optionLabel: 'CropName',
                                columnThatModifies: 'cropInterceptionPk',
                                reloadCore: params?.data?.reloadCore,
                                disabled: !isProjectOwnershipValid,
                            }
                        },
                        filter: 'agSetColumnFilter',
                        filterParams: {
                            cellRenderer: (pParams: any) => {
                                return Utils.formatFilterValues(pParams, options.cropList, 'CropListPk', 'CropName')
                            },
                        },
                    },
                    {
                        headerName: 'Crop (Interception)',
                        wrapText: true,
                        field: 'cropDriftInterceptionPk',
                        type: 'select',
                        resizable: true,
                        width: 270,
                        editable: false,
                        hide: (options.geographySelected == Constants.CROP_GEOGRAPHIES.UK),
                        filter: 'agSetColumnfilter',
                        filterParams: {
                            cellRenderer: (pParams: any) => {
                                return Utils.formatFilterValues(pParams, options.cropList, 'CropListPk', 'CropName')
                            },
                        },
                        cellRenderer: CropDropdownListRenderer,
                        cellRendererParams: (params: any) => {
                            return {
                                options: options.cropList,
                                purpose: Constants.CROP_PURPOSES.DRIFT_INTERCEPTION,
                                matchings: options.cropListMatching,
                                compartment: Constants.COMPARTMENTS.SURFACE_WATER,
                                geography: options.geographySelected,
                                optionValue: 'CropListPk',
                                optionLabel: 'CropName',
                                columnThatModifies: 'cropDriftInterceptionPk',
                                reloadCore: params?.data?.reloadCore,
                                disabled: !isProjectOwnershipValid,
                            }
                        }
                    },
                    {
                        headerName: 'Crop (Spray)',
                        wrapText: true,
                        field: 'cropSprayPk',
                        hide: (options.geographySelected != Constants.CROP_GEOGRAPHIES.UK),
                        resizable: true,
                        width: 300,
                        editable: false,
                        type: 'select',
                        filter: 'agSetColumnFilter',
                        filterParams: {
                            cellRenderer: (pParams: any) => {
                                return Utils.formatFilterValues(pParams, options.cropList, 'CropListPk', 'CropName')
                            },
                        },
                        cellRenderer: CropDropdownListRenderer,
                        cellRendererParams: (params: any) => {
                            return {
                                options: options.cropList,
                                purpose: Constants.CROP_PURPOSES.SPRAY,
                                matchings: options.cropListMatching,
                                compartment: Constants.COMPARTMENTS.SURFACE_WATER,
                                geography: options.geographySelected,
                                optionValue: 'CropListPk',
                                optionLabel: 'CropName',
                                columnThatModifies: 'cropSprayPk',
                                reloadCore: params?.data?.reloadCore,
                                disabled: !isProjectOwnershipValid,
                            }
                        },
                    },
                    {
                        headerName: 'Crop (EVA)',
                        wrapText: true,
                        field: 'cropEVAPk',
                        hide: options.geographySelected !== Constants.CROP_GEOGRAPHIES.EUROPE_UNION || !options.selectedModels.includes(Constants.MODELS.SWAN),
                        resizable: true,
                        width: 220,
                        type: 'select',
                        editable: false,
                        cellRenderer: CropDropdownListRenderer,
                        cellRendererParams: (params: any) => {
                            return {
                                options: cropListEVA,
                                purpose: Constants.CROP_PURPOSES.SPRAY,
                                matchings: options.cropListMatching,
                                compartment: Constants.COMPARTMENTS.SURFACE_WATER,
                                geography: options.geographySelected,
                                optionValue: 'CropListPk',
                                optionLabel: 'CropName',
                                columnThatModifies: 'cropEVAPk',
                                reloadCore: params?.data?.reloadCore,
                                disabled: !isProjectOwnershipValid,
                            }
                        },
                        filter: 'agSetColumnFilter',
                        filterParams: {
                            cellRenderer: (pParams: any) => {
                                return Utils.formatFilterValues(pParams, options.cropList, 'CropListPk', 'CropName')
                            },
                        },
                    },
                    {
                        headerName: 'Crop',
                        wrapText: true,
                        field: 'cropPk',
                        type: 'select',
                        resizable: true,
                        width: 160,
                        editable: false,
                        hide: (options.geographySelected != Constants.CROP_GEOGRAPHIES.USA),
                        filter: 'agSetColumnfilter',
                        filterParams: {
                            cellRenderer: (pParams: any) => {
                                return Utils.formatFilterValues(pParams, options.cropList, 'CropListPk', 'CropName')
                            },
                        },
                        cellRenderer: DropdownListRenderer,
                        cellRendererParams: {
                            options: options.cropList,
                            optionValue: 'CropListPk',
                            optionLabel: 'CropName',
                            columnThatModifies: 'cropPk',
                            disabled: !isProjectOwnershipValid,

                        }
                    },
                    {
                        headerName: 'Number of Applications',
                        wrapText: true,
                        field: 'numberOfApplications',
                        type: 'select',
                        resizable: true,
                        width: 120,
                        editable: false,
                        filter: 'agSetColumnfilter',
                        cellRenderer: DropdownListRenderer,
                        cellRendererParams: (params: any) => {
                            return {
                                instance: this,
                                options: options.numerOfApplications,
                                optionValue: 'value',
                                optionLabel: 'value',
                                columnThatModifies: 'numberOfApplications',
                                height: '300px',
                                disabled: !isProjectOwnershipValid,
                            };
                        }
                    },
                    {
                        headerName: `Variable rates or intervals for ${Constants.MODELS.SWASH}`,
                        wrapText: true,
                        field: 'hasVariableRates',
                        width: 175,
                        minWidth: 175,
                        editable: false,
                        hide: options.geographySelected != Constants.CROP_GEOGRAPHIES.EUROPE_UNION,
                        type: 'select',
                        suppressSizeToFit: true,
                        filter: 'agSetColumnfilter',
                        filterParams: {
                            valueFormatter: (params: any) => {
                                return params.value ? Constants.AFFIRMATIVE_VALUES.YES : !Utils.isEmptyValue(params.value) ? Constants.AFFIRMATIVE_VALUES.NO : '(Blanks)';
                            }
                        },
                        cellRenderer: DropdownListRenderer,
                        cellRendererParams: (params: any) => {
                            return {
                                options: [{ value: true, key: Constants.AFFIRMATIVE_VALUES.YES }, { value: false, key: Constants.AFFIRMATIVE_VALUES.NO }],
                                columnThatModifies: 'hasVariableRates',
                                disabled: !isProjectOwnershipValid || params.data.numberOfApplications == 1 ? true : false,
                                optionLabel: 'key',
                                optionValue: 'value',
                                parent: this,
                            };
                        },
                        cellStyle: (params: any): { [key: string]: string } | null => {
                            if (params.data.numberOfApplications == 1) {
                                return { pointerEvents: 'none', backgroundColor: '#f0f0f0' };
                            }
                            return !isProjectOwnershipValid ? { pointerEvents: 'none', backgroundColor: '#f0f0f0' } : null;
                        },
                        wrapHeaderText: true,
                    },
                    {
                        headerName: '',
                        field: 'expandButton',
                        minWidth: 150,
                        width: 150,
                        cellRenderer: ButtonExpandGridRenderer,
                        cellRendererParams: (params: any) => {
                            return {
                                context: this,
                                dataComponent: this,
                                showLabel: 'Enter Inputs',
                                hideLabel: 'Hide Inputs',
                                showIcon: 'pi pi-arrow-down',
                                hideIcon: 'pi pi-arrow-right',
                                visible: params.data.numberOfApplications !== 1 && params.data.hasVariableRates === true && isProjectOwnershipValid,
                            }
                        },
                        resizable: true,
                        suppressSizeToFit: true,
                        hide: options.geographySelected != Constants.CROP_GEOGRAPHIES.EUROPE_UNION,
                        type: 'default',
                        autoHeight: true,
                        editable: false,
                        cellStyle: (params: any): { [key: string]: string } | null => {
                            if (params.data.numberOfApplications == 1 || params.data.hasVariableRates == false) {
                                return { pointerEvents: 'none', backgroundColor: '#f0f0f0' };
                            }
                            return !isProjectOwnershipValid ? { pointerEvents: 'none', backgroundColor: '#f0f0f0' } : { pointerEvents: 'auto', backgroundColor: '' };
                        },
                        wrapText: true,
                        wrapHeaderText: true,
                    },
                    {
                        headerName: 'Application Interval (days)',
                        wrapText: true,
                        field: 'applicationInterval',
                        type: 'default',
                        resizable: true,
                        width: 125,
                        filter: 'agSetColumnfilter',
                        cellRenderer: InputNumberRenderer,
                        cellRendererParams: (params: any) => {
                            return {
                                disabled: !isProjectOwnershipValid || params.data.numberOfApplications === 1,
                            }
                        },
                        editable: false,
                    },
                ]
            },
            {
                headerName: 'Rate (g/ha)',
                children: [
                    {
                        headerName: 'Default Active Ingredient',
                        wrapText: true,
                        field: 'defaultAI',
                        cellRenderer: '',
                        editable: false,
                    },
                ]
            },
            {
                headerName: '',
                children: [
                    {
                        headerName: 'BBCH (earliest)',
                        wrapText: true,
                        field: 'bbchEarliest',
                        type: 'select',
                        width: 100,
                        resizable: true, filter: 'agSetColumnfilter',
                        editable: false,
                        cellRenderer: InputSearchRenderer,
                        cellRendererParams: (params: any) => {
                            return {
                                instance: this,
                                options: options.bbch,
                                optionValue: 'value',
                                optionLabel: 'value',
                                columnThatModifies: 'bbchEarliest',
                                forceSelection: true,
                                disabled: !isProjectOwnershipValid,
                            };
                        }
                    },
                    {
                        headerName: 'Crop Coverage',
                        wrapText: true,
                        field: 'cropCoverage',
                        type: 'default',
                        filter: 'agSetColumnFilter',
                        resizable: true,
                        cellRenderer: InputTextRenderer,
                        cellRendererParams: {
                            disabled: true,
                        },
                        editable: false,
                    },
                    {
                        headerName: `Change Interception`,
                        wrapText: true,
                        field: 'useSwashCropInterception',
                        width: 130,
                        editable: false,
                        type: 'select',
                        suppressSizeToFit: true,
                        filter: 'agSetColumnfilter',
                        filterParams: {
                            valueFormatter: (params: any) => {
                                return params.value ? Constants.AFFIRMATIVE_VALUES.YES : !Utils.isEmptyValue(params.value) ? Constants.AFFIRMATIVE_VALUES.NO : '(Blanks)';
                            }
                        },
                        cellRenderer: DropdownListRenderer,
                        cellRendererParams: (params: any) => {
                            return {
                                options: [{ value: true, key: Constants.AFFIRMATIVE_VALUES.YES }, { value: false, key: Constants.AFFIRMATIVE_VALUES.NO }],
                                columnThatModifies: 'useSwashCropInterception',
                                disabled: !isProjectOwnershipValid ? true : false,
                                optionLabel: 'key',
                                optionValue: 'value',
                                parent: this,
                            };
                        },
                        wrapHeaderText: true,
                    },
                    {
                        headerName: 'SWASH Crop Interception (%)',
                        wrapText: true,
                        field: 'swashCropInterception',
                        type: 'default',
                        filter: 'agSetColumnFilter',
                        width: 130,
                        resizable: true,
                        cellRenderer: InputNumberRenderer,
                        cellRendererParams: (params: any) => {
                            return {
                                disabled: !isProjectOwnershipValid || !params.data.useSwashCropInterception,
                            }
                        },
                        editable: false,
                        cellStyle: (params: any): { [key: string]: string } | null => {
                            if (!params.data.useSwashCropInterception) {
                                return { pointerEvents: 'none', backgroundColor: '#f0f0f0', color: '#2c2c2c' };
                            }
                            return !isProjectOwnershipValid ? { backgroundColor: '#f0f0f0' } : null;
                        },
                    },
                    {
                        headerName: 'Step 1 and 2 Crop Interception (%)',
                        wrapText: true,
                        field: 'cropInterception',
                        type: 'default',
                        filter: 'agSetColumnFilter',
                        width: 140,
                        resizable: true,
                        cellRenderer: InputNumberRenderer,
                        cellRendererParams: {
                            disabled: !isProjectOwnershipValid,
                        },
                        editable: false,
                    },
                    {
                        headerName: 'First Application Date',
                        wrapText: true,
                        wrapHeaderText: true,
                        field: 'ukApplicationDate',
                        type: 'default',
                        filter: 'agSetColumnFilter',
                        width: 110,
                        resizable: true,
                        editable: false,
                        cellRenderer: CalendarRenderer,
                        cellRendererParams: (params: any) => {
                            const firstDate = params?.data?.applicationSchemeXApplicationWindow?.length > 0;
                            if (firstDate) {
                                params.data.ukApplicationDate = params?.data?.applicationSchemeXApplicationWindow[0].firstDate;
                            }
                            return {
                                instance: this,
                                columnThatModifies: 'ukApplicationDate',
                                highlightDate: new Date(2001, 0, 1),
                                disabled: !isProjectOwnershipValid,
                            };
                        },
                    },
                    {
                        headerName: 'Application Window',
                        wrapText: true,
                        field: 'appWindow',
                        type: 'default',
                        filter: 'agSetColumnFilter',
                        resizable: true,
                        width: 125,
                        editable: false,
                        hide: options.geographySelected !== Constants.CROP_GEOGRAPHIES.EUROPE_UNION || !options.selectedModels.includes(Constants.MODELS.SWASH),
                        cellRenderer: ViewButtonRenderer,
                    },
                    {
                        headerName: 'First Application Date',
                        wrapText: true,
                        field: 'firstApplicationDate',
                        type: 'default',
                        filter: 'agSetColumnFilter',
                        resizable: true,
                        width: 150,
                        editable: false,
                        hide: options.geographySelected === Constants.CROP_GEOGRAPHIES.EUROPE_UNION || options.geographySelected === Constants.CROP_GEOGRAPHIES.UK,
                        cellRenderer: CalendarRenderer,
                        cellRendererParams: (params: any) => {
                            const firstDate = params?.data?.applicationSchemeXApplicationWindow?.length > 0;
                            if (firstDate) {
                                params.data.firstApplicationDate = firstDate
                            }
                            return {
                                instance: this,
                                columnThatModifies: 'firstApplicationDate',
                                highlightDate: new Date(2001, 0, 1),
                                disabled: !isProjectOwnershipValid,
                            };
                        }
                    },
                    {
                        headerName: 'Application Method',
                        wrapText: true,
                        field: 'applicationMethod',
                        type: 'select',
                        resizable: true,
                        width: 200,
                        filter: 'agSetColumnfilter',
                        editable: false,
                        cellRenderer: DropdownListRenderer,
                        cellRendererParams: (params: any) => {
                            return {
                                instance: this,
                                options: options.applicationMethods,
                                optionValue: 'value',
                                optionLabel: 'value',
                                columnThatModifies: 'applicationMethod',
                                disabled: !isProjectOwnershipValid,
                            };
                        }
                    },
                    {
                        headerName: 'Chemical Application Method',
                        wrapText: true,
                        field: 'chemicalApplicationMethod',
                        type: 'select',
                        resizable: true,
                        width: 240,
                        filter: 'agSetColumnfilter',
                        editable: false,
                        cellRenderer: DropdownListRenderer,
                        cellRendererParams: (params: any) => {
                            const node = params.node;
                            let selectedAppMethod = node.data.applicationMethod;
                            let chemicalAppMethods = Constants.APPLICATION_METHOD_VALUES_SURFACEWATER[selectedAppMethod]?.map((key: string) => ({ value: key }));
                            return {
                                instance: this,
                                options: chemicalAppMethods ?? [],
                                optionValue: 'value',
                                optionLabel: 'value',
                                columnThatModifies: 'chemicalApplicationMethod',
                                disabled: !isProjectOwnershipValid,
                            };
                        }
                    },
                    {
                        headerName: 'Soil Depth (cm)',
                        wrapText: true,
                        field: 'soilDepth',
                        type: 'default',
                        filter: 'agSetColumnFilter',
                        resizable: true,
                        width: 110,
                        cellRenderer: InputNumberRenderer,
                        cellRendererParams: {
                            disabled: !isProjectOwnershipValid,
                        },
                        editable: false,
                    },
                    {
                        headerName: 'Step 4 Mitigations',
                        wrapText: true,
                        field: 'step4Mitigation',
                        type: 'default',
                        filter: 'agSetColumnFilter',
                        resizable: true,
                        width: 125,
                        editable: false,
                        hide: options.geographySelected !== Constants.CROP_GEOGRAPHIES.EUROPE_UNION || !options.selectedModels.includes(Constants.MODELS.SWAN),
                        cellRenderer: ViewButtonRenderer,
                        cellRendererParams: {
                            label: 'Set',
                        },
                    },
                ]
            },
            {
                colId: 'depositionGroup',
                field: 'depositionGroup',
                hide: !options.useDeposition,
                headerName: 'Rates for Deposition (g/ha)',
                width: 320,
                wrapText: true,
                resizable: true,
                wrapHeaderText: true,
                autoHeaderHeight: true,
                headerClass: 'ag-header-cell-wrap',
                editable: (params: any) => isProjectOwnershipValid && params.data?.deposition === true,
                cellRendererParams: (params: any) => {
                    return {
                        disabled: !isProjectOwnershipValid || params.data?.deposition !== true,
                    }
                },
                cellStyle: (params: any): { [key: string]: string } | null => {
                    if (params.data?.deposition !== true) {
                        return { pointerEvents: 'none', backgroundColor: '#f0f0f0', color: '#2c2c2c' };
                    }
                    return !isProjectOwnershipValid ? { backgroundColor: '#f0f0f0' } : null;
                },

                children: [


                ]
            },
            {
                headerName: 'SWAN Crop Interception (%)',
                wrapText: true,
                hide:!options.useDeposition,
                field: 'swanCropInterception',
                type: 'default',
                filter: 'agSetColumnFilter',
                width: 130,
                resizable: true,
                cellRenderer: InputNumberRenderer,
                cellRendererParams: (params: any) => {
                    return {
                        disabled: !isProjectOwnershipValid || !params.data.useSwashCropInterception || !params.data.applicationSchemeXApplicationWindow.some((appWindow: any) => appWindow.deposition),
                    }
                },
                editable: false,
                cellStyle: (params: any): { [key: string]: string } | null => {
                    if (!params.data.useSwashCropInterception || !params.data.applicationSchemeXApplicationWindow.some((appWindow: any) => appWindow.deposition)) {
                        return { pointerEvents: 'none', backgroundColor: '#f0f0f0', color: '#2c2c2c' };
                    }
                    return !isProjectOwnershipValid ? { backgroundColor: '#f0f0f0' } : null;
                },
            },
            {
                headerName: '',
                colId: 'duplicate',
                field: 'duplicateButton',
                cellRenderer: DuplicateButtonRenderer,
                type: 'rightAligned',
                width: 130,
                cellRendererParams: {
                    disabled: !isProjectOwnershipValid,
                    mainTablePkName: "applicationSchemePk"
                }

            },
            {
                headerName: '',
                colId: 'delete',
                field: 'deleteButton',
                cellRenderer: DeleteButtonRenderer,
                editable: false,
                type: 'rightAligned',
                width: 125,
                resizable: true,
                cellRendererParams: {
                    disabled: !isProjectOwnershipValid,
                }
            }
        ];
    }

    public getApplicationColumnsDefinition(modelsSelected: (string | undefined)[] | undefined, vaporPressureValues: EndpointGet[], parentRowNode?: RowNode, isProjectOwnershipValid?: boolean): (ColDef | ColGroupDef)[] {
        return [
            {
                headerName: '',
                children: [
                    {
                        headerName: '',
                        field: 'select',
                        type: 'default',
                        headerCheckboxSelection: isProjectOwnershipValid,
                        checkboxSelection: true,
                        width: 50,
                        hide: false,
                        editable: false,
                        pinned: 'left',
                        cellStyle: () => {
                            return !isProjectOwnershipValid ? { pointerEvents: 'none', backgroundColor: '#f0f0f0' }
                                : undefined;
                        }
                    },
                    {
                        headerName: 'Location',
                        field: 'location',
                        wrapText: true,
                        type: 'default',
                        width: 95,
                        resizable: true,
                        filter: 'agSetColumnFilter',
                        cellEditor: InputTextRenderer,
                        cellRenderer: (params: any) => {
                            const node = params.node;
                            if (parentRowNode) {
                                const isSelected = node.data && parentRowNode.data.applicationSchemeXApplicationWindow?.some((x: any) => node.data.applicationDateSurfaceWaterPk === x.applicationDateSurfaceWaterPk);
                                if (isSelected) {
                                    node.setSelected(true);
                                }
                            }
                            return node.data.location;
                        },
                        cellRendererParams: (params: any) => {
                            return {
                                disabled: true
                            };
                        },
                    },
                    {
                        headerName: 'Beginning Date',
                        field: 'beginWindow',
                        wrapText: true,
                        flex: 1.5,
                        type: 'default',
                        filter: 'agSetColumnFilter',
                        resizable: true,
                        cellRenderer: CalendarRenderer,
                        editable: false,
                        cellRendererParams: (params: any) => {
                            return {
                                columnThatModifies: 'beginWindow',
                                highlightDate: new Date(2001, 0, 1),
                                disabled: !isProjectOwnershipValid,
                                showCopyToAll: true
                            };
                        },
                    },
                    {
                        headerName: 'DayNr',
                        field: 'beginWindowJulian',
                        editable: false,
                        flex: 0.8,
                        type: 'default',
                        filter: 'agSetColumnFilter',
                        resizable: true,
                        cellRenderer: InputNumberRenderer,
                        cellRendererParams: (params: any) => {
                            return {
                                instance: this,
                                field: 'beginWindowJulian',
                                maxDecimals: 0,
                                min: 1,
                                max: 365,
                                excludeRange: true,
                                useGrouping: false,
                            }
                        },
                        valueGetter: (params: any) => {
                            const beginWindowDate = params.data.beginWindow ? params.data.beginWindow : (parentRowNode?.data.cropWithoutAppDates ? new Date(2001, 0, 1) : params.data.beginWindow);
                            if (beginWindowDate) {
                                let dayNumber = Utils.getDayOfYear(beginWindowDate);
                                params.data.beginWindowJulian = dayNumber;
                                return Utils.getDayOfYear(beginWindowDate);
                            }
                            return null;
                        },
                    },
                    {
                        headerName: 'End Date',
                        field: 'endWindow',
                        wrapText: true,
                        flex: 1.3,
                        type: 'default',
                        filter: 'agSetColumnFilter',
                        resizable: true,
                        cellRenderer: CalendarRenderer,
                        editable: false,
                        cellRendererParams: (params: any) => {
                            return {
                                instance: this,
                                columnThatModifies: 'endWindow',
                                highlightDate: new Date(2001, 0, 1),
                                disabled: !isProjectOwnershipValid,
                            };
                        },
                    },
                    {
                        headerName: 'DayNr',
                        field: 'endWindowJulian',
                        wrapText: true,
                        editable: false,
                        flex: 0.8,
                        type: 'default',
                        filter: 'agSetColumnFilter',
                        resizable: true,
                        cellRenderer: InputNumberRenderer,
                        cellRendererParams: (params: any) => {
                            return {
                                instance: this,
                                field: 'endWindowJulian',
                                maxDecimals: 0,
                                min: 1,
                                max: 365,
                                excludeRange: true,
                                useGrouping: false,
                            }
                        },
                        valueGetter: (params: any) => {
                            const endWindow = params.data.endWindow ? params.data.endWindow : (parentRowNode?.data.cropWithoutAppDates ? new Date(2001, 0, 1) : params.data.endWindow);
                            if (endWindow) {
                                let dayNumber = Utils.getDayOfYear(endWindow);
                                params.data.endWindowJulian = dayNumber;
                                return dayNumber;
                            }
                            return null;
                        },
                    },
                    {
                        headerName: 'Emergence Day',
                        field: 'emergencyDate',
                        editable: false,
                        flex: 1,
                        wrapText: true,
                        type: 'default',
                        filter: 'agSetColumnFilter',
                        valueFormatter: Utils.convertDateWithoutYear,
                        resizable: true,
                        valueGetter: (params: any) => {
                            const emergencyDate = parentRowNode?.data.cropWithoutAppDates ? new Date(1999, 0, 1) : params.data.emergencyDate;
                            if (emergencyDate) {
                                return emergencyDate;
                            }
                            return null;
                        },
                    },
                    {
                        headerName: 'Harvest Day',
                        field: 'harvestDate',
                        editable: false,
                        flex: 1,
                        wrapText: true,
                        type: 'default',
                        filter: 'agSetColumnFilter',
                        valueFormatter: Utils.convertDateWithoutYear,
                        resizable: true,
                        valueGetter: (params: any) => {
                            const harvestDate = parentRowNode?.data.cropWithoutAppDates ? new Date(1999, 0, 31) : params.data.harvestDate;
                            if (harvestDate) {
                                return harvestDate;
                            }
                            return null;
                        },
                    }]
            },
            {
                headerName: 'Step 4 Mitigations',
                children: [
                    {
                        headerName: Constants.COLUMN_HEADER_NAMES.RUN_OFF,
                        field: Constants.FIELD_NAMES.RUN_OFF,
                        headerComponent: MitigationCheckboxHeaderComponent,
                        headerComponentParams: (params: any) => {
                            return {
                                isProjectOwnershipValid: isProjectOwnershipValid,
                            };
                        },
                        editable: isProjectOwnershipValid,
                        wrapText: true,
                        flex: 1,
                        resizable: true,
                        hide: !(modelsSelected && modelsSelected.includes(Constants.MODELS.SWAN)),
                        filter: 'agSetColumnFilter',
                        cellRenderer: MitigationCheckboxRenderer,
                        cellRendererParams: (params: any) => {
                            return {
                                columnThatModifies: Constants.FIELD_NAMES.RUN_OFF,
                                isProjectOwnershipValid: isProjectOwnershipValid,
                            };
                        },
                        cellStyle: (params: any) => {
                            return params.data.location.includes('D') ? { pointerEvents: 'none', backgroundColor: '#f0f0f0' }
                                : isProjectOwnershipValid ? undefined : { pointerEvents: 'none', backgroundColor: '#f0f0f0' };
                        }
                    },
                    {
                        headerName: Constants.COLUMN_HEADER_NAMES.SPRAY_DRIFT,
                        field: Constants.FIELD_NAMES.SPRAY_DRIFT,
                        headerComponent: MitigationCheckboxHeaderComponent,
                        headerComponentParams: (params: any) => {
                            return {
                                isProjectOwnershipValid: isProjectOwnershipValid,
                            };
                        },
                        editable: isProjectOwnershipValid,
                        wrapText: true,
                        flex: 1,
                        resizable: true,
                        hide: !(modelsSelected && modelsSelected.includes(Constants.MODELS.SWAN)),
                        filter: 'agSetColumnFilter',
                        cellRenderer: MitigationCheckboxRenderer,
                        cellRendererParams: (params: any) => {
                            return {
                                columnThatModifies: Constants.FIELD_NAMES.SPRAY_DRIFT,
                                isProjectOwnershipValid: isProjectOwnershipValid,
                            };
                        },
                        cellStyle: (params: any) => {
                            return !isProjectOwnershipValid ? { pointerEvents: 'none', backgroundColor: '#f0f0f0' }
                                : undefined;
                        }
                    },
                    {
                        headerName: Constants.COLUMN_HEADER_NAMES.DEPOSITION,
                        field: Constants.FIELD_NAMES.DEPOSITION,
                        headerComponent: MitigationCheckboxHeaderComponent,
                        headerComponentParams: (params: any) => {
                            return {
                                isProjectOwnershipValid: isProjectOwnershipValid,
                            };
                        },
                        editable: isProjectOwnershipValid,
                        wrapText: true,
                        flex: 1,
                        resizable: true,
                        hide: !(modelsSelected && modelsSelected.includes(Constants.MODELS.SWAN)),
                        filter: 'agSetColumnFilter',
                        cellRenderer: MitigationCheckboxRenderer,
                        cellRendererParams: (params: any) => {
                            return {
                                columnThatModifies: Constants.FIELD_NAMES.DEPOSITION,
                                isProjectOwnershipValid: isProjectOwnershipValid,
                            };
                        },
                        cellStyle: (params: any) => {
                            return !isProjectOwnershipValid ? { pointerEvents: 'none', backgroundColor: '#f0f0f0' }
                                : undefined;
                        }
                    }]
            }]
    }

    public getInverseColumnsDefinition(options: any, { useNumberOfApplications, useIntervalBetweenApplications, useBBCH, useApplicationWindow, useRateGHA, useRateLBACRE }: Project, isProjectOwnershipValid: boolean): (ColDef | ColGroupDef)[] {
        const inverseNumOfApplications = {
            headerName: 'Number of Applications',
            wrapText: true,
            children: [
                {
                    headerName: 'Min',
                    field: 'minNumberOfApplications',
                    type: 'select',
                    resizable: true,
                    width: 100,
                    editable: false,
                    filter: 'agSetColumnfilter',
                    cellRenderer: DropdownListRenderer,
                    cellRendererParams: (params: any) => {
                        return {
                            options: options.numerOfApplications,
                            optionValue: 'value',
                            optionLabel: 'value',
                            height: '300px',
                            disabled: !isProjectOwnershipValid,
                        };
                    }
                },
                {
                    headerName: 'Max',
                    field: 'maxNumberOfApplications',
                    type: 'select',
                    resizable: true,
                    width: 100,
                    editable: false,
                    filter: 'agSetColumnfilter',
                    cellRenderer: DropdownListRenderer,
                    cellRendererParams: (params: any) => {
                        return {
                            options: options.numerOfApplications,
                            optionValue: 'value',
                            optionLabel: 'value',
                            height: '300px',
                            disabled: !isProjectOwnershipValid,
                        };
                    }
                },
                {
                    headerName: 'Increment',
                    field: 'incrementNumberOfApplications',
                    type: 'select',
                    resizable: true,
                    width: 100,
                    filter: 'agSetColumnfilter',
                    cellRenderer: DropdownListRenderer,
                    cellRendererParams: (params: any) => {
                        return {
                            options: options.numerOfApplications,
                            optionValue: 'value',
                            optionLabel: 'value',
                            height: '300px',
                            disabled: !isProjectOwnershipValid,
                        };
                    }
                }]
        };
        const numberOfApplications = {
            headerName: 'Number of Applications',
            wrapText: true,
            field: 'numberOfApplications',
            type: 'select',
            resizable: true,
            width: 120,
            editable: false,
            filter: 'agSetColumnfilter',
            cellRenderer: DropdownListRenderer,
            cellRendererParams: (params: any) => {
                return {
                    options: options.numerOfApplications,
                    optionValue: 'value',
                    optionLabel: 'value',
                    columnThatModifies: 'numberOfApplications',
                    height: '300px',
                    disabled: !isProjectOwnershipValid,
                };
            }
        };
        const inverseApplicationInterval = {
            headerName: 'Application Interval (days)',
            wrapText: true,
            children: [
                {
                    headerName: 'Min',
                    field: 'minApplicationInterval',
                    type: 'default',
                    resizable: true,
                    width: 125,
                    filter: 'agSetColumnfilter',
                    cellRenderer: InputNumberRenderer,
                    cellRendererParams: {
                        disabled: !isProjectOwnershipValid
                    },
                    editable: false,
                },
                {
                    headerName: 'Max',
                    field: 'maxApplicationInterval',
                    type: 'default',
                    resizable: true,
                    width: 125,
                    filter: 'agSetColumnfilter',
                    cellRenderer: InputNumberRenderer,
                    cellRendererParams: {
                        disabled: !isProjectOwnershipValid,
                    },
                    editable: false,
                },
                {
                    headerName: 'Increment',
                    field: 'incrementApplicationInterval',
                    type: 'default',
                    resizable: true,
                    width: 125,
                    filter: 'agSetColumnfilter',
                    cellRenderer: InputNumberRenderer,
                    cellRendererParams: {
                        disabled: !isProjectOwnershipValid,
                    },
                    editable: false,
                }],
        };
        const applicationInterval = {
            headerName: 'Application Interval (days)',
            wrapText: true,
            field: 'applicationInterval',
            type: 'default',
            resizable: true,
            width: 150,
            filter: 'agSetColumnfilter',
            cellRenderer: InputNumberRenderer,
            cellRendererParams: (params: any) => {
                return {
                    disabled: !isProjectOwnershipValid || params.data.numberOfApplications === 1,
                }
            },
            editable: false,
        };

        const inverseBBCH = {
            headerName: 'BBCH (earliest)',
            wrapText: true,
            children: [
                {
                    headerName: 'Min',
                    field: 'minBbchEarliest',
                    type: 'select',
                    width: 100,
                    resizable: true, filter: 'agSetColumnfilter',
                    editable: false,
                    cellRenderer: InputSearchRenderer,
                    cellRendererParams: (params: any) => {
                        return {
                            options: options.bbch,
                            optionValue: 'value',
                            optionLabel: 'value',
                            forceSelection: true,
                            disabled: !isProjectOwnershipValid,
                        };
                    }
                },
                {
                    headerName: 'Max',
                    field: 'maxBbchEarliest',
                    type: 'select',
                    width: 100,
                    resizable: true, filter: 'agSetColumnfilter',
                    editable: false,
                    cellRenderer: InputSearchRenderer,
                    cellRendererParams: (params: any) => {
                        return {

                            options: options.bbch,
                            optionValue: 'value',
                            optionLabel: 'value',
                            instance: this,
                            columnThatModifies: 'maxBbchEarliest',
                            forceSelection: true,
                            disabled: !isProjectOwnershipValid,
                        };
                    }
                },
                {
                    headerName: 'Increment',
                    field: 'incrementBbchEarliest',
                    type: 'select',
                    width: 100,
                    resizable: true, filter: 'agSetColumnfilter',
                    editable: false,
                    cellRenderer: InputSearchRenderer,
                    cellRendererParams: (params: any) => {
                        return {
                            options: options.bbch,
                            optionValue: 'value',
                            optionLabel: 'value',
                            forceSelection: true,
                            disabled: !isProjectOwnershipValid,
                        };
                    }
                }
            ]
        };
        const BBCH = {
            headerName: 'BBCH (earliest)',
            wrapText: true,
            field: 'bbchEarliest',
            type: 'select',
            width: 100,
            resizable: true, filter: 'agSetColumnfilter',
            editable: false,
            cellRenderer: InputSearchRenderer,
            cellRendererParams: (params: any) => {
                return {
                    options: options.bbch,
                    optionValue: 'value',
                    optionLabel: 'value',
                    forceSelection: true,
                    disabled: !isProjectOwnershipValid,
                };
            }
        };

        const RateLBACRE = {
            headerName: 'Rate (lb/acre)',
            wrapText: true,
            field: 'defaultAILBACRE',
            cellRenderer: '',
            editable: isProjectOwnershipValid,
        }
        const inverseRateLBACRE = {
            headerName: 'Rate (lb/acre)',
            children: [
                {
                    headerName: 'Default Active Ingredient',
                    wrapText: true,
                    field: 'defaultAILBACRE',
                    cellRenderer: '',
                    editable: isProjectOwnershipValid,
                },
            ]
        }

        const RateGHA = {
            headerName: 'Rate (g/ha)',
            wrapText: true,
            wrapHeaderText: true,
            field: 'defaultAIGHA',
            cellRenderer: '',
            editable: isProjectOwnershipValid,
        }
        const inverseRateGHA = {
            headerName: 'Rate (g/ha)',
            children: [
                {
                    headerName: 'Default Active Ingredient',
                    wrapText: true,
                    wrapHeaderText: true,
                    field: 'defaultAIGHA',
                    cellRenderer: '',
                    editable: isProjectOwnershipValid,
                },
            ]
        };

        return [
            {
                headerName: '',
                children: [
                    {
                        headerName: '',
                        field: 'select',
                        type: 'default',
                        headerCheckboxSelection: isProjectOwnershipValid,
                        checkboxSelection: true,
                        width: 50,
                        hide: false,
                        editable: false,
                        pinned: 'left',
                        cellStyle: () => {
                            return !isProjectOwnershipValid ? { pointerEvents: 'none', backgroundColor: '#f0f0f0' }
                                : undefined;
                        }
                    },
                    {
                        headerName: 'Application Scheme',
                        wrapText: true,
                        field: 'name',
                        type: 'default',
                        pinned: 'left',
                        resizable: true,
                        width: 300,
                        filter: 'agSetColumnFilter',
                        cellRenderer: InputTextApplicationSchemeRenderer,
                        cellRendererParams: {
                            disabled: !isProjectOwnershipValid,
                            isRequired: true
                        },
                        editable: false,
                        cellEditorParams: {
                            maxLength: 200,
                        },
                    },
                    {
                        headerName: 'Sub-Geography',
                        wrapText: true,
                        field: 'geographies',
                        type: 'default',
                        hide: (options.geographySelected !== Constants.CROP_GEOGRAPHIES.USA),
                        filter: 'agSetColumnFilter',
                        cellRenderer: ListboxRenderer,
                        cellRendererParams: (params: any) => {
                            return {
                                options: options.geographiesList,
                                optionValue: 'key',
                                optionLabel: 'name',
                                checkbox: true,
                                filter: false,
                                multiple: true,
                                disabled: false,
                                validateModel: false,
                                compartment: 'Surface Water',
                            }
                        },
                        resizable: true,
                        sortable: true,
                        width: 150,
                        editable: false,
                    },
                    {
                        headerName: 'Crop (AppDate)',
                        wrapText: true,
                        field: 'cropAppDatePk',
                        type: 'select',
                        resizable: true,
                        width: 200,
                        editable: false,
                        hide: (options.geographySelected == Constants.CROP_GEOGRAPHIES.USA) || (options.geographySelected == Constants.CROP_GEOGRAPHIES.UK),
                        filter: 'agSetColumnfilter',
                        filterParams: {
                            cellRenderer: (pParams: any) => {
                                return Utils.formatFilterValues(pParams, options.cropList, 'CropListPk', 'CropName')
                            },
                        },
                        cellRenderer: CropDropdownListRenderer,
                        cellRendererParams: (params: any) => {
                            return {
                                options: options.cropList,
                                purpose: Constants.CROP_PURPOSES.APP_DATE,
                                matchings: options.cropListMatching,
                                compartment: Constants.COMPARTMENTS.SURFACE_WATER,
                                geography: options.geographySelected,
                                optionValue: 'CropListPk',
                                optionLabel: 'CropName',
                                columnThatModifies: 'cropAppDatePk',
                                reloadCore: params?.data?.reloadCore,
                                disabled: !isProjectOwnershipValid,
                            }
                        },
                    },
                    {
                        headerName: 'Crop (Drift)',
                        wrapText: true,
                        field: 'cropInterceptionPk',
                        hide: (options.geographySelected != Constants.CROP_GEOGRAPHIES.UK),
                        resizable: true,
                        width: 220,
                        type: 'select',
                        editable: false,
                        cellRenderer: CropDropdownListRenderer,
                        cellRendererParams: (params: any) => {
                            return {
                                options: options.cropList,
                                purpose: Constants.CROP_PURPOSES.INTERCEPTION,
                                matchings: options.cropListMatching,
                                compartment: Constants.COMPARTMENTS.SURFACE_WATER,
                                geography: options.geographySelected,
                                optionValue: 'CropListPk',
                                optionLabel: 'CropName',
                                columnThatModifies: 'cropInterceptionPk',
                                reloadCore: params?.data?.reloadCore,
                                disabled: !isProjectOwnershipValid,
                            }
                        },
                        filter: 'agSetColumnFilter',
                        filterParams: {
                            cellRenderer: (pParams: any) => {
                                return Utils.formatFilterValues(pParams, options.cropList, 'CropListPk', 'CropName')
                            },
                        },
                    },
                    {
                        headerName: 'Crop (Interception)',
                        wrapText: true,
                        field: 'cropDriftInterceptionPk',
                        type: 'select',
                        resizable: true,
                        width: 270,
                        editable: false,
                        hide: (options.geographySelected == Constants.CROP_GEOGRAPHIES.UK),
                        filter: 'agSetColumnfilter',
                        filterParams: {
                            cellRenderer: (pParams: any) => {
                                return Utils.formatFilterValues(pParams, options.cropList, 'CropListPk', 'CropName')
                            },
                        },
                        cellRenderer: CropDropdownListRenderer,
                        cellRendererParams: (params: any) => {
                            return {
                                options: options.cropList,
                                purpose: Constants.CROP_PURPOSES.DRIFT_INTERCEPTION,
                                matchings: options.cropListMatching,
                                compartment: Constants.COMPARTMENTS.SURFACE_WATER,
                                geography: options.geographySelected,
                                optionValue: 'CropListPk',
                                optionLabel: 'CropName',
                                columnThatModifies: 'cropDriftInterceptionPk',
                                reloadCore: params?.data?.reloadCore,
                                disabled: !isProjectOwnershipValid,
                            }
                        }
                    },
                    {
                        headerName: 'Crop (Spray)',
                        wrapText: true,
                        field: 'cropSprayPk',
                        hide: (options.geographySelected != Constants.CROP_GEOGRAPHIES.UK),
                        resizable: true,
                        width: 300,
                        editable: true,
                        type: 'select',
                        filter: 'agSetColumnFilter',
                        filterParams: {
                            cellRenderer: (pParams: any) => {
                                return Utils.formatFilterValues(pParams, options.cropList, 'CropListPk', 'CropName')
                            },
                        },
                        cellRenderer: CropDropdownListRenderer,
                        cellRendererParams: (params: any) => {
                            return {
                                options: options.cropList,
                                purpose: Constants.CROP_PURPOSES.SPRAY,
                                matchings: options.cropListMatching,
                                compartment: Constants.COMPARTMENTS.SURFACE_WATER,
                                geography: options.geographySelected,
                                optionValue: 'CropListPk',
                                optionLabel: 'CropName',
                                columnThatModifies: 'cropSprayPk',
                                reloadCore: params?.data?.reloadCore,
                                disabled: !isProjectOwnershipValid,
                            }
                        },
                    },
                    {
                        headerName: 'Crop',
                        field: 'cropPk',
                        type: 'select',
                        resizable: true,
                        width: 160,
                        editable: false,
                        hide: (options.geographySelected != Constants.CROP_GEOGRAPHIES.USA),
                        filter: 'agSetColumnfilter',
                        filterParams: {
                            cellRenderer: (pParams: any) => {
                                return Utils.formatFilterValues(pParams, options.cropList, 'CropListPk', 'CropName')
                            },
                        },
                        cellRenderer: DropdownListRenderer,
                        cellRendererParams: {
                            options: options.cropList,
                            optionValue: 'CropListPk',
                            optionLabel: 'CropName',
                            columnThatModifies: 'cropPk',
                            disabled: !isProjectOwnershipValid,

                        }
                    },
                    useNumberOfApplications ? inverseNumOfApplications : numberOfApplications,
                    useIntervalBetweenApplications ? inverseApplicationInterval : applicationInterval,
                ]
            },
            {
                headerName: 'Rate (g/ha)',
                children: [
                    useRateGHA ? inverseRateGHA : RateGHA,
                ]
            },
            {
                headerName: 'Rate (lb/acre)',
                children: [
                    useRateLBACRE ? inverseRateLBACRE : RateLBACRE,
                ]
            },
            {
                headerName: 'Rate (lb/acre)',
                children: [
                    {
                        headerName: 'Default Active Ingredient',
                        wrapText: true,
                        field: 'defaultAILBACRE',
                        cellRenderer: '',
                        editable: isProjectOwnershipValid,
                    },
                ]
            },
            {
                headerName: '',
                children: [
                    useBBCH ? inverseBBCH : BBCH,
                    {
                        headerName: 'Crop Coverage',
                        wrapText: true,
                        field: 'cropCoverage',
                        type: 'default',
                        filter: 'agSetColumnFilter',
                        resizable: true,
                        width: 150,
                        cellRenderer: InputTextRenderer,
                        cellRendererParams: {
                            disabled: true,
                        },
                        editable: false,
                    },
                    {
                        headerName: 'Interception (%)',
                        wrapText: true,
                        field: 'cropInterception',
                        type: 'default',
                        filter: 'agSetColumnFilter',
                        width: 110,
                        resizable: true,
                        cellRenderer: InputNumberRenderer,
                        cellRendererParams: (params: any) => {
                            return {
                                instance: this,
                                columnThatModifies: 'cropInterception',
                                disabled: !isProjectOwnershipValid,
                            };
                        },
                        editable: false,
                    },
                    {
                        headerName: 'Application Window',
                        wrapText: true,
                        field: 'appWindow',
                        type: 'default',
                        filter: 'agSetColumnFilter',
                        resizable: true,
                        width: 125,
                        editable: false,
                        hide: !useApplicationWindow && options.geographySelected !== Constants.CROP_GEOGRAPHIES.EUROPE_UNION || !options.selectedModels.includes(Constants.MODELS.SWASH),
                        cellRenderer: ViewButtonRenderer,
                    },
                    {
                        headerName: 'First Application Date',
                        wrapText: true,
                        hide: options.geographySelected === Constants.CROP_GEOGRAPHIES.EUROPE_UNION,
                        children: [
                            {
                                headerName: 'Min',
                                field: 'minApplicationDate',
                                type: 'default',
                                filter: 'agSetColumnFilter',
                                width: 150,
                                resizable: true, editable: false,
                                hide: options.geographySelected === Constants.CROP_GEOGRAPHIES.EUROPE_UNION,
                                cellRenderer: CalendarRenderer,
                                cellRendererParams: (params: any) => {
                                    return {
                                        instance: this,
                                        columnThatModifies: 'minApplicationDate',
                                        highlightDate: new Date(2001, 0, 1),
                                        disabled: !isProjectOwnershipValid,
                                    };
                                },
                            },
                            {
                                headerName: 'Max',
                                field: 'maxApplicationDate',
                                type: 'default',
                                filter: 'agSetColumnFilter',
                                width: 150,
                                resizable: true, editable: false,
                                hide: options.geographySelected === Constants.CROP_GEOGRAPHIES.EUROPE_UNION,
                                cellRenderer: CalendarRenderer,
                                cellRendererParams: (params: any) => {
                                    return {
                                        instance: this,
                                        columnThatModifies: 'maxApplicationDate',
                                        highlightDate: new Date(2001, 0, 1),
                                        disabled: !isProjectOwnershipValid,
                                    };
                                },
                            },
                            {
                                headerName: 'Increment',
                                field: 'incrementApplicationDate',
                                type: 'default',
                                filter: 'agSetColumnFilter',
                                width: 100,
                                resizable: true, hide: options.geographySelected === Constants.CROP_GEOGRAPHIES.EUROPE_UNION,
                                cellRenderer: InputNumberRenderer,
                                cellRendererParams: (params: any) => {
                                    return {
                                        instance: this,
                                        columnThatModifies: 'incrementApplicationDate',
                                        disabled: !isProjectOwnershipValid,
                                    };
                                },
                                editable: false,
                            }]
                    },
                    {
                        headerName: 'First Application Date',
                        wrapText: true,
                        field: 'firstApplicationDate',
                        type: 'default',
                        filter: 'agSetColumnFilter',
                        resizable: true,
                        width: 150,
                        editable: false,
                        hide: options.geographySelected === Constants.CROP_GEOGRAPHIES.EUROPE_UNION || options.geographySelected === Constants.CROP_GEOGRAPHIES.UK,
                        cellRenderer: CalendarRenderer,
                        cellRendererParams: (params: any) => {
                            const firstDate = params?.data?.applicationSchemeXApplicationWindow?.length > 0;
                            if (firstDate) {
                                params.data.firstApplicationDate = firstDate
                            }
                            return {
                                instance: this,
                                columnThatModifies: 'firstApplicationDate',
                                highlightDate: new Date(2001, 0, 1),
                                disabled: !isProjectOwnershipValid,
                            };
                        }
                    },
                    {
                        headerName: 'Application Method',
                        wrapText: true,
                        field: 'applicationMethod',
                        type: 'select',
                        resizable: true,
                        width: 200,
                        filter: 'agSetColumnfilter',
                        editable: false,
                        cellRenderer: DropdownListRenderer,
                        cellRendererParams: {
                            options: options.applicationMethods,
                            optionValue: 'value',
                            optionLabel: 'value',
                            disabled: !isProjectOwnershipValid,
                        }
                    },
                    {
                        headerName: 'Chemical Application Method',
                        wrapText: true,
                        field: 'chemicalApplicationMethod',
                        type: 'select',
                        resizable: true,
                        width: 240,
                        filter: 'agSetColumnfilter',
                        editable: false,
                        cellRenderer: DropdownListRenderer,
                        cellRendererParams: (params: any) => {
                            let chemicalAppMethods = Constants.APPLICATION_METHOD_VALUES_SURFACEWATER[params.node.data.applicationMethod]?.map((key: string) => ({ value: key }));
                            return {
                                options: chemicalAppMethods ?? [],
                                optionValue: 'value',
                                optionLabel: 'value',
                                disabled: !isProjectOwnershipValid,
                            };
                        }
                    },
                    {
                        headerName: 'Soil Depth (cm)',
                        wrapText: true,
                        field: 'soilDepth',
                        type: 'default',
                        filter: 'agSetColumnFilter',
                        resizable: true,
                        width: 110,
                        cellRenderer: InputNumberRenderer,
                        cellRendererParams: {
                            disabled: !isProjectOwnershipValid,
                        },
                        editable: false,
                    },
                    {
                        headerName: 'Nozzle drift reduction (10m buffer distance)',
                        wrapText: true,
                        field: 'nozzleDriftReduction10m',
                        type: 'default',
                        filter: 'agSetColumnFilter',
                        resizable: true,
                        width: 170,
                        cellRenderer: InputNumberRenderer,
                        cellRendererParams: {
                            disabled: !isProjectOwnershipValid,
                        },
                        editable: false,
                    },
                    {
                        headerName: 'Nozzle drift reduction (20m buffer distance)',
                        wrapText: true,
                        field: 'nozzleDriftReduction20m',
                        type: 'default',
                        filter: 'agSetColumnFilter',
                        resizable: true,
                        width: 170,
                        cellRenderer: InputNumberRenderer,
                        cellRendererParams: {
                            disabled: !isProjectOwnershipValid,
                        },
                        editable: false,
                    }
                ]
            },
            {
                headerName: '',
                colId: 'delete',
                field: 'deleteButton',
                cellRenderer: DeleteButtonRenderer,
                editable: false,
                type: 'rightAligned',
                width: 125,
                resizable: true,
                cellRendererParams: {
                    disabled: !isProjectOwnershipValid,
                }
            }
        ];
    }

    public getInverseApplicationColumnsDefinition(parentRowNode?: RowNode, isProjectOwnershipValid?: boolean): (ColDef | ColGroupDef)[] {
        return [
            {
                headerName: '',
                children: [
                    {
                        headerName: 'Location',
                        headerCheckboxSelection: true,
                        checkboxSelection: true,
                        field: 'location',
                        editable: false,
                        type: 'default',
                        flex: 1.5,
                        resizable: true,
                        filter: 'agSetColumnFilter',
                        cellEditor: 'agTextCellEditor',
                        sort: "asc",
                        cellRenderer: (params: any) => {
                            const node = params.node;
                            if (parentRowNode) {
                                const isSelected = node.data && parentRowNode.data.applicationSchemeXApplicationWindow?.some((x: any) => node.data.applicationDateSurfaceWaterPk === x.applicationDateSurfaceWaterPk);
                                if (isSelected) {
                                    node.setSelected(true);
                                }
                            }
                            return node.data.location;
                        },
                        cellRendererParams: (params: any) => {
                            return {
                                instance: this,
                            };
                        },
                    }
                ],
            },
            {
                headerName: 'Beginning Date',
                wrapText: true,
                children: [
                    {
                        headerName: 'Min',
                        field: 'min',
                        type: 'default',
                        flex: 1.2,
                        filter: 'agSetColumnFilter',
                        resizable: true,
                        hide: false,
                        editable: false,
                        //validationType: 'string',
                        cellRenderer: CalendarRenderer,
                        cellRendererParams: (params: any) => {
                            return {
                                instance: this,
                                columnThatModifies: 'min',
                                highlightDate: new Date(2001, 0, 1),
                                disabled: !isProjectOwnershipValid,
                            };
                        },
                    },
                    {
                        headerName: 'Max',
                        field: 'max',
                        type: 'default',
                        flex: 1.2,
                        filter: 'agSetColumnFilter',
                        resizable: true,
                        hide: false,
                        editable: false,
                        //validationType: 'string',
                        cellRenderer: CalendarRenderer,
                        cellRendererParams: (params: any) => {
                            return {
                                instance: this,
                                columnThatModifies: 'max',
                                highlightDate: new Date(2001, 0, 1),
                                disabled: !isProjectOwnershipValid,
                            };
                        },
                    },
                    {
                        headerName: 'Increment',
                        field: 'increment',
                        type: 'default',
                        filter: 'agSetColumnFilter',
                        flex: 1,
                        resizable: true,
                        hide: false,
                        cellRenderer: InputNumberRenderer,
                        cellRendererParams: (params: any) => {
                            return {
                                instance: this,
                                columnThatModifies: 'increment',
                                disabled: !isProjectOwnershipValid,
                            };
                        },
                        editable: false,
                    }
                ]
            },
            {
                headerName: '',
                children:
                    [
                        {
                            headerName: 'DayNr',
                            field: 'beginWindowJulian',
                            wrapText: true,
                            flex: 0.9,
                            editable: false,
                            hide: true,
                            type: 'default',
                            filter: 'agSetColumnFilter',
                            resizable: true,
                            valueGetter: (params: any) => {
                                const beginWindowDate = parentRowNode?.data.cropWithoutAppDates ? new Date(2001, 0, 1) : params.data.beginWindow;
                                if (beginWindowDate) {
                                    return Utils.getDayOfYear(beginWindowDate);
                                }
                                return null;
                            },
                        }
                    ]
            },
            {
                headerName: '',
                children:
                    [
                        {
                            headerName: 'End Date',
                            field: 'endWindow',
                            flex: 1.2,
                            type: 'default',
                            filter: 'agSetColumnFilter',
                            resizable: true,
                            editable: false,
                            hide: true,
                            cellRenderer: CalendarRenderer,
                            cellRendererParams: (params: any) => {
                                return {
                                    instance: this,
                                    columnThatModifies: 'endWindow',
                                    highlightDate: new Date(2001, 0, 1),
                                    disabled: !isProjectOwnershipValid,
                                };
                            },
                        }
                    ]
            },
            {
                headerName: '',
                children:
                    [
                        {
                            headerName: 'DayNr',
                            field: 'endWindowJulian',
                            editable: false,
                            hide: true,
                            flex: 0.9,
                            type: 'default',
                            filter: 'agSetColumnFilter',
                            resizable: true,
                            cellRenderer: InputNumberRenderer,
                            cellRendererParams: (params: any) => {
                                params.data.isInverseModeling = true;
                                return {
                                    instance: this,
                                    field: 'endWindowJulian',
                                    maxDecimals: 0,
                                    min: 1,
                                    max: 365,
                                    excludeRange: true,
                                    useGrouping: false,
                                }
                            },
                            valueGetter: (params: any) => {
                                const endWindow = parentRowNode?.data.cropWithoutAppDates ? new Date(2001, 0, 1) : params.data.endWindow;
                                if (endWindow) {
                                    return Utils.getDayOfYear(endWindow);
                                }
                                return null;
                            },
                        }
                    ]
            },
            {
                headerName: '',
                children:
                    [
                        {
                            headerName: 'Emergence Day',
                            field: 'emergencyDate',
                            flex: 1,
                            editable: false,
                            type: 'default',
                            filter: 'agSetColumnFilter',
                            valueFormatter: Utils.convertDate,
                            resizable: true
                        }
                    ]
            },
            {
                headerName: '',
                children:
                    [
                        {
                            headerName: 'Harvest Day',
                            field: 'harvestDate',
                            editable: false,
                            flex: 1,
                            type: 'default',
                            filter: 'agSetColumnFilter',
                            valueFormatter: Utils.convertDate,
                            resizable: true
                        }
                    ]
            }
        ]
    }

    public getMitigationXApplicationSchemeColumnsDefinition(isProjectOwnershipValid: boolean, parantInstance: any, scenariosToEnable: string[]): (ColDef<any, any> | ColGroupDef<any>)[] {
        return [
            {
                headerName: '',
                children: [
                    {
                        headerName: 'Mitigation Name',
                        wrapText: true,
                        field: 'mitigationName',
                        type: 'default',
                        resizable: true,
                        width: 250,
                        filter: 'agSetColumnFilter',
                        cellRenderer: InputTextApplicationSchemeRenderer,
                        cellRendererParams: {
                            fieldNameToValidate: 'mitigationName',
                            fieldPk: 'mitigationsXApplicationSchemePk',
                            disabled: !isProjectOwnershipValid,
                            isRequired: true
                        },
                        editable: false,
                        cellEditorParams: {
                            maxLength: 200,
                        },
                    },]
            },
            {
                headerName: Constants.COLUMN_HEADER_NAMES.RUN_OFF,
                children: [
                    {
                        headerName: 'Width of vegetated buffer zones (m)',
                        wrapText: true,
                        field: 'bufferZone',
                        resizable: true,
                        width: 110,
                        editable: true,
                        type: 'select',
                        filter: 'agSetColumnFilter',
                        cellRenderer: BufferDropdownListRenderer,
                        cellRendererParams: (params: any) => {
                            return {
                                options: [{ value: 5, key: 5 }, { value: 10, key: 10 }, { value: 15, key: 15 }, { value: 20, key: 20 }],
                                columnThatModifies: 'bufferZone',
                                disabled: !isProjectOwnershipValid || !scenariosToEnable.includes(Constants.FIELD_NAMES.RUN_OFF),
                                optionLabel: 'key',
                                optionValue: 'value',
                                parent: this,
                            };
                        },
                        cellStyle: (params: any) => {
                            return !scenariosToEnable.includes(Constants.FIELD_NAMES.RUN_OFF) ? { pointerEvents: 'none', backgroundColor: '#f0f0f0' }
                                : isProjectOwnershipValid ? undefined : { pointerEvents: 'none', backgroundColor: '#f0f0f0' };
                        }
                    },
                    {
                        headerName: 'Fractional reduction in run-off volume',
                        field: 'fractionalReductionRunoffVolume',
                        width: 110,
                        resizable: true,
                        filter: 'agSetColumnFilter',
                        type: 'default',
                        cellRenderer: InputNumberRenderer,
                        cellRendererParams: (params: any) => {
                            return {
                                disabled: !isProjectOwnershipValid || !scenariosToEnable.includes(Constants.FIELD_NAMES.RUN_OFF),
                                maxDecimals: 4,
                            };
                        },
                        cellStyle: (params: any) => {
                            return !scenariosToEnable.includes(Constants.FIELD_NAMES.RUN_OFF) ? { pointerEvents: 'none', backgroundColor: '#f0f0f0' }
                                : isProjectOwnershipValid ? undefined : { pointerEvents: 'none', backgroundColor: '#f0f0f0' };
                        }
                    },
                    {
                        headerName: 'Fractional reduction in run-off flux',
                        field: 'fractionalReductionRunoffFlux',
                        width: 110,
                        resizable: true,
                        filter: 'agSetColumnFilter',
                        type: 'default',
                        cellRenderer: InputNumberRenderer,
                        cellRendererParams: (params: any) => {
                            return {
                                disabled: !isProjectOwnershipValid || !scenariosToEnable.includes(Constants.FIELD_NAMES.RUN_OFF),
                                maxDecimals: 4,
                            };
                        },
                        cellStyle: (params: any) => {
                            return !scenariosToEnable.includes(Constants.FIELD_NAMES.RUN_OFF) ? { pointerEvents: 'none', backgroundColor: '#f0f0f0' }
                                : isProjectOwnershipValid ? undefined : { pointerEvents: 'none', backgroundColor: '#f0f0f0' };
                        }
                    },
                    {
                        headerName: 'Fractional reduction in erosion mass',
                        field: 'fractionalReductionErosionMass',
                        width: 110,
                        resizable: true,
                        filter: 'agSetColumnFilter',
                        type: 'default',
                        cellRenderer: InputNumberRenderer,
                        cellRendererParams: (params: any) => {
                            return {
                                disabled: !isProjectOwnershipValid || !scenariosToEnable.includes(Constants.FIELD_NAMES.RUN_OFF),
                                maxDecimals: 4,
                            };
                        },
                        cellStyle: (params: any) => {
                            return !scenariosToEnable.includes(Constants.FIELD_NAMES.RUN_OFF) ? { pointerEvents: 'none', backgroundColor: '#f0f0f0' }
                                : isProjectOwnershipValid ? undefined : { pointerEvents: 'none', backgroundColor: '#f0f0f0' };
                        }
                    },
                    {
                        headerName: 'Fractional reduction in erosion flux',
                        field: 'fractionalReductionErosionFlux',
                        width: 110,
                        resizable: true,
                        filter: 'agSetColumnFilter',
                        type: 'default',
                        cellRenderer: InputNumberRenderer,
                        cellRendererParams: (params: any) => {
                            return {
                                disabled: !isProjectOwnershipValid || !scenariosToEnable.includes(Constants.FIELD_NAMES.RUN_OFF),
                                maxDecimals: 4,
                            };
                        },
                        cellStyle: (params: any) => {
                            return !scenariosToEnable.includes(Constants.FIELD_NAMES.RUN_OFF) ? { pointerEvents: 'none', backgroundColor: '#f0f0f0' }
                                : isProjectOwnershipValid ? undefined : { pointerEvents: 'none', backgroundColor: '#f0f0f0' };
                        }
                    },
                ]
            },
            {
                headerName: Constants.COLUMN_HEADER_NAMES.SPRAY_DRIFT,
                children: [
                    {
                        headerName: 'Nozzle reduction (%)',
                        field: 'nozzleReduction',
                        width: 110,
                        resizable: true,
                        filter: 'agSetColumnFilter',
                        type: 'default',
                        cellRenderer: InputNumberRenderer,
                        cellRendererParams: (params: any) => {
                            return {
                                disabled: !isProjectOwnershipValid || !scenariosToEnable.includes(Constants.FIELD_NAMES.SPRAY_DRIFT) || this.validateSprayDriftNozzle(params),
                                maxDecimals: 4,
                            };
                        },
                        cellStyle: (params: any) => {
                            return !scenariosToEnable.includes(Constants.FIELD_NAMES.SPRAY_DRIFT) || this.validateSprayDriftNozzle(params) ? { pointerEvents: 'none', backgroundColor: '#f0f0f0' }
                                : isProjectOwnershipValid ? undefined : { pointerEvents: 'none', backgroundColor: '#f0f0f0' };
                        }
                    },
                    {
                        headerName: 'Spray Drift options',
                        field: 'sprayDriftOption',
                        width: 250,
                        resizable: true,
                        filter: 'agSetColumnFilter',
                        type: 'default',
                        cellRenderer: SprayDriftOptionsRenderer,
                        cellRendererParams: {
                            disabled: !isProjectOwnershipValid || !scenariosToEnable.includes(Constants.FIELD_NAMES.SPRAY_DRIFT),
                        },
                        cellStyle: (params: any) => {
                            return !scenariosToEnable.includes(Constants.FIELD_NAMES.SPRAY_DRIFT) ? { pointerEvents: 'none', backgroundColor: '#f0f0f0' }
                                : isProjectOwnershipValid ? undefined : { pointerEvents: 'none', backgroundColor: '#f0f0f0' };
                        }
                    },
                    {
                        headerName: 'Buffer width (m)',
                        field: 'sprayDriftBuffer',
                        width: 110,
                        resizable: true,
                        filter: 'agSetColumnFilter',
                        type: 'default',
                        cellRenderer: InputNumberRenderer,
                        cellRendererParams: (params: any) => {
                            return {
                                disabled: !isProjectOwnershipValid || this.validateSprayDrifBufferStyle(params) || !scenariosToEnable.includes(Constants.FIELD_NAMES.SPRAY_DRIFT),
                                maxDecimals: 4,
                            };
                        },
                        cellStyle: (params: any) => {
                            return this.validateSprayDrifBufferStyle(params) || !scenariosToEnable.includes(Constants.FIELD_NAMES.SPRAY_DRIFT) ? { pointerEvents: 'none', backgroundColor: '#f0f0f0' }
                                : isProjectOwnershipValid ? undefined : { pointerEvents: 'none', backgroundColor: '#f0f0f0' };
                        }
                    },
                    {
                        headerName: 'Pond mass loading',
                        field: 'pondMassLoading',
                        width: 110,
                        resizable: true,
                        filter: 'agSetColumnFilter',
                        type: 'default',
                        cellRenderer: InputNumberRenderer,
                        cellRendererParams: (params: any) => {
                            return {
                                disabled: !isProjectOwnershipValid || this.validatePondMassLoadingStyle(params) || !scenariosToEnable.includes(Constants.FIELD_NAMES.SPRAY_DRIFT),
                                maxDecimals: 4,
                            };
                        },
                        cellStyle: (params: any) => {
                            return this.validatePondMassLoadingStyle(params) || !scenariosToEnable.includes(Constants.FIELD_NAMES.SPRAY_DRIFT) ? { pointerEvents: 'none', backgroundColor: '#f0f0f0' }
                                : isProjectOwnershipValid ? undefined : { pointerEvents: 'none', backgroundColor: '#f0f0f0' };
                        }
                    },
                    {
                        headerName: 'Ditch mass loading',
                        field: 'ditchMassLoading',
                        width: 110,
                        resizable: true,
                        filter: 'agSetColumnFilter',
                        type: 'default',
                        cellRenderer: InputNumberRenderer,
                        cellRendererParams: (params: any) => {
                            return {
                                disabled: !isProjectOwnershipValid || this.validateDitchMassLoadingStyle(params) || !scenariosToEnable.includes(Constants.FIELD_NAMES.SPRAY_DRIFT),
                                maxDecimals: 4,
                            };
                        },
                        cellStyle: (params: any) => {
                            return this.validateDitchMassLoadingStyle(params) || !scenariosToEnable.includes(Constants.FIELD_NAMES.SPRAY_DRIFT) ? { pointerEvents: 'none', backgroundColor: '#f0f0f0' }
                                : isProjectOwnershipValid ? undefined : { pointerEvents: 'none', backgroundColor: '#f0f0f0' };
                        }
                    },
                    {
                        headerName: 'Stream mass loading',
                        field: 'streamMassLoading',
                        width: 110,
                        resizable: true,
                        filter: 'agSetColumnFilter',
                        type: 'default',
                        cellRenderer: InputNumberRenderer,
                        cellRendererParams: (params: any) => {
                            return {
                                disabled: !isProjectOwnershipValid || this.validateStreamMassLoadingStyle(params) || !scenariosToEnable.includes(Constants.FIELD_NAMES.SPRAY_DRIFT),
                                maxDecimals: 4,
                            };
                        },
                        cellStyle: (params: any) => {
                            return this.validateStreamMassLoadingStyle(params) || !scenariosToEnable.includes(Constants.FIELD_NAMES.SPRAY_DRIFT) ? { pointerEvents: 'none', backgroundColor: '#f0f0f0' }
                                : isProjectOwnershipValid ? undefined : { pointerEvents: 'none', backgroundColor: '#f0f0f0' };
                        }
                    },
                ]
            },
            {
                headerName: Constants.COLUMN_HEADER_NAMES.DEPOSITION,
                children: [
                    {
                        headerName: 'Distance (m)',
                        wrapText: true,
                        field: 'distance',
                        resizable: true,
                        width: 100,
                        editable: true,
                        type: 'select',
                        filter: 'agSetColumnFilter',
                        cellRenderer: CropDropdownListRenderer,
                        cellRendererParams: (params: any) => {
                            return {
                                options: [{ value: 1, key: 1 }, { value: 5, key: 5 }, { value: 10, key: 10 }, { value: 15, key: 15 }, { value: 20, key: 20 }],
                                columnThatModifies: 'distance',
                                disabled: !isProjectOwnershipValid || !scenariosToEnable.includes(Constants.FIELD_NAMES.DEPOSITION),
                                optionLabel: 'key',
                                optionValue: 'value',
                                parent: this,
                            };
                        },
                        cellStyle: (params: any) => {
                            return !scenariosToEnable.includes(Constants.FIELD_NAMES.DEPOSITION) ? { pointerEvents: 'none', backgroundColor: '#f0f0f0' }
                                : isProjectOwnershipValid ? undefined : { pointerEvents: 'none', backgroundColor: '#f0f0f0' };
                        }
                    },]
            },
            {
                headerName: '',
                colId: 'duplicate',
                field: 'duplicateButton',
                cellRenderer: DuplicateButtonRenderer,
                type: 'rightAligned',
                width: 130,
                cellRendererParams: {
                    disabled: !isProjectOwnershipValid,
                    fieldNameToValidate: 'mitigationName',
                    mainTablePkName: "mitigationsXApplicationSchemePk"
                }

            },
            {
                headerName: '',
                colId: 'delete',
                field: 'deleteButton',
                cellRenderer: DeleteButtonRenderer,
                editable: false,
                type: 'rightAligned',
                width: 125,
                resizable: true,
                cellRendererParams: {
                    disabled: !isProjectOwnershipValid,
                    parantInstance: parantInstance
                }
            }
        ]
    }

    validateSprayDrifBufferStyle(params: any): boolean {
        if (!params.data.sprayDriftOption) {
            return true;
        }
        if (params.data.sprayDriftOption && (params.data.sprayDriftOption.includes('SDO_01') || params.data.sprayDriftOption.includes('SDO_03'))) {
            return true;
        }
        return false;
    }

    validateSprayDriftNozzle(params: any): boolean {
        if (params.data.sprayDriftOption && (params.data.sprayDriftOption.includes('SDO_03'))) {
            return true;
        }
        return false;
    }

    validatePondMassLoadingStyle(params: any): boolean {
        if (!params.data.sprayDriftOption) {
            return true;
        }
        if (params.data.sprayDriftOption.includes('SDO_01') || params.data.sprayDriftOption.includes('SDO_02')) {
            return true;
        }
        return false;
    }

    validateDitchMassLoadingStyle(params: any): boolean {
        if (!params.data.sprayDriftOption) {
            return true;
        }
        if (params.data.sprayDriftOption.includes('SDO_01') || params.data.sprayDriftOption.includes('SDO_02')) {
            return true;
        }
        return false;
    }

    validateStreamMassLoadingStyle(params: any): boolean {
        if (!params.data.sprayDriftOption) {
            return true;
        }
        if (params.data.sprayDriftOption.includes('SDO_01') || params.data.sprayDriftOption.includes('SDO_02')) {
            return true;
        }
        return false;
    }

    configureDetailGrid(options: any, isProjectOwnershipValid: boolean): ColGroupDef[] {
        return [
            {
                headerName: '',
                children: [
                    {
                        headerName: 'Application Number',
                        field: 'application_number',
                        width: 110,
                        resizable: true,
                        filter: 'agSetColumnFilter',
                        type: 'default',
                        editable: false,
                        sort: "asc",
                        cellRenderer: InputNumberRenderer,
                        cellRendererParams: (params: any) => {
                            return {
                                disabled: true,
                            };
                        },
                    },
                ]
            },
            {
                headerName: 'Rate (g/ha)',
                children: [
                    {
                        headerName: 'Default Active Ingredient',
                        field: 'defaultAI',
                        cellRenderer: '',
                        editable: !isProjectOwnershipValid,
                    },
                ]
            },
        ]
    }
}
